import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListCheck, faClose } from '@fortawesome/free-solid-svg-icons'
import { useClickOutside } from '../hooks/useClickOutside'
import AIRPNumberInput from '../../components/common/AIRPNumberInput/AIRPNumberInput'
import Button from '../../components/common/Button/Button'
import DatePicker from '../../components/common/DatePicker/DatePicker'
import { Option } from '../../components/common/Dropdown/Dropdown'
import ToolTip from '../../components/common/ToolTip/ToolTip'
import IncomeUnitType from '../../types/unit/IncomeUnit'
import { useReadOnly } from '../../utils/ReadOnlyContext'

export type ValueType = "string" | "number" | "date" | "boolean" | "integer" | "currency"

export type ApplyColumnInfo = {
    key: string;
    display?: string;
    value?: ValueType;
    type?: ValueType;
    dropdown?: Option[]
}

interface ApplyToAllProps {
    columns: ApplyColumnInfo[];
    handleApplyToAll: (key: keyof IncomeUnitType, value: string | number | boolean | null | undefined) => void
}

export default function ApplyToAll<T extends object>(props: ApplyToAllProps) {
    const ref = useRef<HTMLDivElement>(null)
    const { isReadOnly } = useReadOnly()
    const { t } = useTranslation(["lease_table", "common"])
    const { columns } = props

    const [isPopoverOpen, setIsPopoverOpen] = useState(false)
    const [currentColumn, setCurrentColumn] = useState<ApplyColumnInfo>()
    const [applyValue, setApplyValue] = useState<T[keyof T]>()

    const clearForm = () => {
        setCurrentColumn(undefined)
        setApplyValue(undefined)
    }

    const chooseColumn = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const columnName = e.target.value
        const column = columns.find((c) => c.key === columnName)
        if (column) {
            setCurrentColumn(column)
        }
    }

    const getColumnDisplay = (column: ApplyColumnInfo): string => {
        return column.display ? column.display : column.key
    }

    const getApplyInput = () => {
        if (!currentColumn) {
            return <input name="applyValue" className='internal-header'
                type="text" aria-label='applyValue' disabled />
        }

        if (currentColumn.dropdown) {
            return <select name="applyValue" className='internal-header width-100' value={applyValue as string}
                aria-label='applyValue'
                onChange={(e) => setApplyValue(e.target.value as T[keyof T])}>
                {
                    currentColumn.dropdown.map((dropdown, index) => {
                        return (
                            <option key={index} value={dropdown.value} disabled={dropdown.disabled}>
                                {dropdown.text}
                            </option>
                        )
                    })
                }
            </select>
        } else {
            switch (currentColumn.type) {
                case "string":
                    return <input name="applyValue" className='internal-header width-100'
                        value={applyValue as string} type="text" autoComplete='off'
                        aria-label='applyValue'
                        onChange={(e) => setApplyValue(e.target.value as T[keyof T])}></input>
                case "number":
                    return <AIRPNumberInput formatType="decimal" name="applyValue" initialValue={applyValue as number | null}
                        readOnly={false} className='border-hover limited-input internal-header width-100'
                        aria-label='applyValue'
                        onChange={(value: number | null) => setApplyValue(value as T[keyof T])} />
                case "currency":
                    return <AIRPNumberInput formatType="currency" symbol="$" name="applyValue" initialValue={applyValue as number | null}
                        readOnly={false} className='border-hover limited-input internal-header width-100'
                        aria-label='applyValue'
                        onChange={(value: number | null) => setApplyValue(value as T[keyof T])} />
                case "integer":
                    return <AIRPNumberInput formatType="integer" name="applyValue" initialValue={applyValue as number | null}
                        readOnly={false} className='border-hover limited-input internal-header width-100'
                        aria-label='applyValue'
                        onChange={(value: number | null) => setApplyValue(value as T[keyof T])} />
                case "date":
                    return <DatePicker name="applyValue" id="applyValue" className='internal-header width-100'
                        date={applyValue as string} aria-label='applyValue'
                        onChange={(value) => setApplyValue(value?.toISOString() as T[keyof T])} />
                default:
                    return null
            }
        }
    }

    const submitApplyValueForm = (e: React.FormEvent<HTMLFormElement>) => {
        const formData: FormData = new FormData(e.target as HTMLFormElement);
        const formValues = Object.fromEntries(formData.entries()) as Record<string, string>;
        const columnName = formValues["applyToClumn"]
        const applyValue = formValues["applyValue"]
        const column = columns.find((c) => c.key === columnName)
        if (column) {
            let columnValue = null
            if (applyValue) {
                const type = column.type
                switch (type) {
                    case "string":
                        columnValue = applyValue
                        break;
                    case "integer":
                        columnValue = Number(applyValue)
                        if (isNaN(columnValue)) {
                            toast.error("Invalid number")
                            return
                        }
                        columnValue = Math.trunc(columnValue)
                        break;
                    case "number":
                    case "currency":
                        columnValue = Number(applyValue)
                        if (isNaN(columnValue)) {
                            toast.error("Invalid number")
                            return
                        }
                        break;
                }
            }
            props.handleApplyToAll(columnName as keyof IncomeUnitType, columnValue)
        }
    }

    useClickOutside(ref, () => {
        setIsPopoverOpen(false)
    })

    return (
        <div ref={ref} className="apply-popover">
            <ToolTip contents={
                <Button
                    onClick={() => {
                        clearForm()
                        setIsPopoverOpen(!isPopoverOpen)
                    }}
                    className='internal-header'
                    text={<FontAwesomeIcon icon={faListCheck} />}
                    disabled={isReadOnly || !(columns && columns.length > 0)}
                    ariaLabel="toggleApplyToAll" />
            }>
                {t('tooltips.apply-tooltip')}
            </ToolTip>
            {isPopoverOpen &&
                <div className="popover-content">
                    <div className="text-right close-icon">
                        <FontAwesomeIcon icon={faClose} onClick={() => setIsPopoverOpen(false)} />
                    </div>
                    <form onChange={(e) => e.stopPropagation()} onFocus={(e) => e.stopPropagation()}
                        onSubmit={(e) => {
                            e.preventDefault();
                            submitApplyValueForm(e)
                            setIsPopoverOpen(false)
                        }}>
                        <div>
                            <div>Choose column to which apply value:</div>
                            <select name="applyToClumn"
                                value={currentColumn ? currentColumn.key : ""}
                                onChange={(e) => chooseColumn(e)}
                                className='internal-header' aria-label='applyColumn'>
                                <option value="" disabled></option>
                                {
                                    columns.map((col, key) => (
                                        <option key={key} value={col.key}>{getColumnDisplay(col)}</option>
                                    ))
                                }
                            </select>

                            <div>
                                <div>Apply Value:</div>
                                <div>
                                    {getApplyInput()}
                                </div>
                            </div>

                        </div>
                        <div>
                            <Button type="submit" className='internal-header' ariaLabel='submitApplyToAll'>
                                Apply
                            </Button>
                        </div>
                    </form>
                </div>
            }
        </div>
    )
}
